import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getUnreadNotificationCount,
  listNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "../services/notificationApi";
import {
  getNotificationRoom,
  subscribeNotificationSocket,
} from "../services/notificationSocket";
import {
  getNotificationLink,
  mergeNotificationPages,
} from "../utils/notificationUtils";

const PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 60 * 1000;

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
};

const hasToken = () => Boolean(localStorage.getItem("token"));

const useNotifications = ({ onNavigate, onRealtimeNotification } = {}) => {
  const room = useMemo(() => getNotificationRoom(readStoredUser()), []);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  // ใช้กันผลลัพธ์ของ request เก่าเขียนทับรายการที่ refetch ใหม่กว่า
  const listRequestRef = useRef(0);
  const isOpenRef = useRef(false);
  const callbacksRef = useRef({ onNavigate, onRealtimeNotification });
  callbacksRef.current = { onNavigate, onRealtimeNotification };

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const refreshUnreadCount = useCallback(async () => {
    if (!hasToken()) return;
    try {
      setUnreadCount(await getUnreadNotificationCount());
    } catch (requestError) {
      console.error("Failed to load unread notification count:", requestError);
    }
  }, []);

  // refetch หน้าแรกแล้วแทนที่รายการทั้งชุด (แจ้งเตือนที่ถูก withdraw จะหายไปเอง)
  const refreshList = useCallback(async () => {
    if (!hasToken()) return null;
    const requestId = ++listRequestRef.current;
    setLoading(true);
    setError("");

    try {
      const result = await listNotifications({ page: 1, limit: PAGE_SIZE });
      if (requestId !== listRequestRef.current) return null;

      setItems(result.data);
      setUnreadCount(result.unreadCount);
      setPage(1);
      setTotalPages(Number(result.pagination.totalPages) || 0);
      return result.data;
    } catch (requestError) {
      console.error("Failed to load notifications:", requestError);
      if (requestId === listRequestRef.current) {
        setError("ไม่สามารถโหลดการแจ้งเตือนได้");
      }
      return null;
    } finally {
      if (requestId === listRequestRef.current) setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || page >= totalPages) return;
    const requestId = listRequestRef.current;
    const nextPage = page + 1;
    setLoadingMore(true);
    setError("");

    try {
      const result = await listNotifications({ page: nextPage, limit: PAGE_SIZE });
      if (requestId !== listRequestRef.current) return;

      setItems((prev) => mergeNotificationPages(prev, result.data));
      setUnreadCount(result.unreadCount);
      setPage(Number(result.pagination.page) || nextPage);
      setTotalPages(Number(result.pagination.totalPages) || 0);
    } catch (requestError) {
      console.error("Failed to load more notifications:", requestError);
      if (requestId === listRequestRef.current) {
        setError("โหลดการแจ้งเตือนเพิ่มเติมไม่สำเร็จ");
      }
    } finally {
      setLoadingMore(false);
    }
  }, [loading, loadingMore, page, totalPages]);

  const open = useCallback(() => {
    setIsOpen(true);
    refreshList();
  }, [refreshList]);

  const close = useCallback(() => setIsOpen(false), []);

  const toggle = useCallback(() => {
    if (isOpenRef.current) close();
    else open();
  }, [open, close]);

  const openNotification = useCallback(
    (notification) => {
      if (!notification) return;

      if (!notification.isRead) {
        setItems((prev) =>
          prev.map((item) =>
            item._id === notification._id
              ? { ...item, isRead: true, readAt: new Date().toISOString() }
              : item
          )
        );
        setUnreadCount((prev) => Math.max(prev - 1, 0));

        // ไม่ await: ถ้า API error ก็ยังเปิดหน้าได้ แค่ sync badge ใหม่
        markNotificationAsRead(notification._id).catch((requestError) => {
          console.error("Failed to mark notification as read:", requestError);
          refreshUnreadCount();
        });
      }

      const link = getNotificationLink(notification);
      if (link) {
        setIsOpen(false);
        callbacksRef.current.onNavigate?.(link);
      }
    },
    [refreshUnreadCount]
  );

  const markAllAsRead = useCallback(async () => {
    if (unreadCount === 0) return true;
    const readAt = new Date().toISOString();
    setItems((prev) =>
      prev.map((item) => (item.isRead ? item : { ...item, isRead: true, readAt }))
    );
    setUnreadCount(0);

    try {
      await markAllNotificationsAsRead();
      return true;
    } catch (requestError) {
      console.error("Failed to mark all notifications as read:", requestError);
      if (isOpenRef.current) refreshList();
      else refreshUnreadCount();
      return false;
    }
  }, [unreadCount, refreshList, refreshUnreadCount]);

  // socket payload ไม่มี link จึง refetch แล้วใช้รายการล่าสุด
  const openLatestNotification = useCallback(async () => {
    const latestItems = await refreshList();
    if (latestItems?.length) openNotification(latestItems[0]);
  }, [refreshList, openNotification]);

  useEffect(
    () =>
      subscribeNotificationSocket(room, (payload) => {
        refreshUnreadCount();
        if (isOpenRef.current) refreshList();
        callbacksRef.current.onRealtimeNotification?.(payload);
      }),
    [room, refreshUnreadCount, refreshList]
  );

  // fallback เผื่อ socket หลุด: poll ทุก 60 วินาที และเช็กใหม่เมื่อกลับมาที่แท็บ
  useEffect(() => {
    refreshUnreadCount();

    const intervalId = window.setInterval(() => {
      if (!document.hidden) refreshUnreadCount();
    }, POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (!document.hidden) refreshUnreadCount();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshUnreadCount]);

  return {
    items,
    unreadCount,
    isOpen,
    loading,
    loadingMore,
    error,
    hasMore: page < totalPages,
    open,
    close,
    toggle,
    refreshList,
    loadMore,
    openNotification,
    openLatestNotification,
    markAllAsRead,
  };
};

export default useNotifications;
